import '../../styles/InteractiveComponents.css';

import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';
import {
  LessonComponentResponseType,
  SliderComponent
} from '../../models/LessonComponent';

import DeleteIcon from '@mui/icons-material/Delete';
import { CustomSlider as DisabledSlider } from './SliderCustomized';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../shared/components/IconButtonWithConfirmation';
import { RadioGroup } from '@mui/material';
import React from 'react';
import { SliderItem } from '../../models/LessonComponentContent';
import { SliderLearnerView } from './SliderLearnerView';
import { SliderOptionItem } from './SliderOptionItem';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';
import styles from '../../styles/Slider.module.scss';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonEditContext } from '../../models/LessonEditContext';

interface SliderProps {
  component: SliderComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

export const Slider = (props: SliderProps) => {
  const { handleSettingsChange } = useLessonEditContext();
  const { mode } = useLessonContext();

  const items = props.component.content.slider || [];
  const isSurvey = props.component?.settings.survey;
  const correctResponsePattern = isSurvey
    ? ''
    : props.component.content.correctResponsePattern || null;
  /** Placeholder item displayed during edit mode to add new items. */
  const blankSliderItem: SliderItem = { id: nanoid(), label: '', image: null };

  /** select a default option for author mode. */
  const handleAddItem = (item: SliderItem) => {
    const copyItems = cloneDeep(items);
    copyItems.push(item);
    const copy = cloneDeep(props.component);
    copy.content.slider = copyItems;
    if (copyItems.length > 0 && !correctResponsePattern) {
      copy.content.correctResponsePattern = copyItems[0].id;
    }
    handleSettingsChange(copy);
  };

  const handleItemChange = (item: SliderItem) => {
    const copyItems = cloneDeep(items);
    const index = copyItems.findIndex((i) => i.id === item.id);
    copyItems[index] = item;
    const copy = cloneDeep(props.component);
    copy.content.slider = copyItems;
    handleSettingsChange(copy);
  };

  /** Delete the given item. */
  const handleDeleteItem = (id: string) => {
    const copyItems = cloneDeep(items);
    const optionIndex = copyItems.findIndex((i) => i.id === id);
    copyItems.splice(optionIndex, 1);
    const copy = cloneDeep(props.component);
    /** set correctResponsePattern as null if the deleted option is the correctResponsePatttern  */
    if (copyItems.length > 0) {
      if (correctResponsePattern === id) {
        copy.content.correctResponsePattern = copyItems[0].id;
      }
    } else {
      copy.content.correctResponsePattern = null;
    }
    copy.content.slider = copyItems;
    handleSettingsChange(copy);
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    const copyItems = cloneDeep(items);
    const movingItem = copyItems[fromIndex];
    copyItems.splice(fromIndex, 1);
    copyItems.splice(toIndex, 0, movingItem);
    const copy = cloneDeep(props.component);
    copy.content.slider = copyItems;
    handleSettingsChange(copy);
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  /** Save author's correct answer */
  const handleRadioOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      const correctResponse = event.target.value;
      const copy = cloneDeep(props.component);
      copy.content.correctResponsePattern = correctResponse;
      handleSettingsChange(copy);
    }
  };

  const radioGroupName = `group-${props.component.componentkey}`;

  return (
    <Grid container direction="column" className={styles.parentComp}>
      {mode === 'edit' ? (
        <Grid container direction="column">
          <Grid className={`${styles.sliderIcon} ${styles.sliderIconAuthor}`}>
            <DisabledSlider min={0} max={1} defaultValue={0.5} disabled />
          </Grid>

          <Grid>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selection-options">
                {(provided1, snapshot1) => (
                  <div
                    ref={provided1.innerRef}
                    style={getDroppableStyle(snapshot1.isDraggingOver)}
                  >
                    <RadioGroup
                      name={radioGroupName}
                      value={correctResponsePattern}
                      className={styles.gridStretch}
                    >
                      {items?.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(provided2) => (
                            <div
                              ref={provided2.innerRef}
                              {...provided2.draggableProps}
                            >
                              <Grid
                                container
                                direction="row"
                                alignItems="center"
                              >
                                <SliderOptionItem
                                  componentKey={props.component.componentkey}
                                  item={item}
                                  onItemChange={handleItemChange}
                                  radioOptionChange={
                                    isSurvey
                                      ? undefined
                                      : handleRadioOptionChange
                                  }
                                />
                                <Grid
                                  {...provided2.dragHandleProps}
                                  style={{ paddingTop: '3px' }}
                                >
                                  <span
                                    style={{
                                      color: 'rgba(0,0,0,0.54)' //* to make it look the same as the 'delete' icon button
                                    }}
                                  >
                                    <DragIndicatorIcon />
                                  </span>
                                </Grid>
                                <Grid>
                                  <IconButtonWithConfirmation
                                    size="small"
                                    onConfirm={() => handleDeleteItem(item?.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButtonWithConfirmation>
                                </Grid>
                              </Grid>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </RadioGroup>
                    {provided1.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {
              <Grid container direction="row" alignItems="center">
                <SliderOptionItem
                  componentKey={props.component.componentkey}
                  item={blankSliderItem}
                  onItemChange={handleAddItem}
                  radioOptionChange={handleRadioOptionChange}
                />
              </Grid>
            }
          </Grid>
        </Grid>
      ) : (
        <SliderLearnerView
          component={props.component}
          items={items}
          onChange={props.onChange}
        />
      )}
    </Grid>
  );
};
